<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';
import KanbanPriorityBadge from './components/KanbanPriorityBadge.vue';

const store = useStore();
const { t } = useI18n();

const selectedBoardId = ref('');
const cardQuery = ref('');
const priorityFilter = ref('all');

const isCardDetailOpen = ref(false);
const isCardFormOpen = ref(false);
const isBoardModalOpen = ref(false);
const isColumnModalOpen = ref(false);
const isSummaryModalOpen = ref(false);
const isDeleteModalOpen = ref(false);
const selectedCard = ref(null);
const editingBoard = ref(null);
const editingColumn = ref(null);
const showBoardActionsDropdown = ref(false);
const activeColumnActionsId = ref('');
const deleteTarget = ref(null);
const isBoardSettingsOpen = ref(false);
const isMigrateModalOpen = ref(false);
const migrateStep = ref(1);
const migrateTargetBoardId = ref('');
const columnMappings = ref({});
const isMigrating = ref(false);
const isSettingUpFunnel = ref(false);
const boardSettingsDraft = reactive({
  name: '',
  description: '',
  entry_column_public_id: '',
  is_default: false,
});
const cardDetailTab = ref('details');

const cardActivities = ref([]);
const cardTasks = ref([]);
const newComment = ref('');
const newTaskTitle = ref('');
const isPostingComment = ref(false);
const isAddingTask = ref(false);

const boardDraft = reactive({ name: '', description: '' });
const columnDraft = reactive({
  name: '',
  color: '#3B82F6',
  board_public_id: '',
  wip_limit: '',
});
const cardDraft = reactive({
  title: '',
  description: '',
  column_public_id: '',
  priority: 'normal',
  conversation_display_id: '',
  interest_product_public_id: '',
  assignee_id: '',
  value_cents: '',
});

const uiFlags = computed(() => store.getters['kanban/getUIFlags']);
const boards = computed(() => store.getters['kanban/getBoards']);
const products = computed(() => store.getters['products/getProducts']);
const currentBoard = computed(
  () =>
    boards.value.find(board => board.public_id === selectedBoardId.value) ||
    boards.value[0] ||
    {}
);
const boardColumns = computed(() => currentBoard.value.columns || []);
const selectedBoardName = computed(() => currentBoard.value?.name || '');
const canDeleteBoard = computed(() => !!currentBoard.value?.public_id);
const totalColumns = computed(() => boardColumns.value.length);
const totalCards = computed(() =>
  boardColumns.value.reduce(
    (sum, column) => sum + (column.cards?.length || 0),
    0
  )
);
const hasActiveCardFilters = computed(
  () => !!cardQuery.value.trim() || priorityFilter.value !== 'all'
);

const filteredColumns = computed(() => {
  const query = cardQuery.value.trim().toLowerCase();
  const activePriority = priorityFilter.value;
  return boardColumns.value.map(column => ({
    ...column,
    cards: (column.cards || []).filter(card => {
      const matchesQuery =
        !query ||
        card.title?.toLowerCase().includes(query) ||
        card.contact?.name?.toLowerCase().includes(query) ||
        card.interest_products?.some(p =>
          p.product_name?.toLowerCase().includes(query)
        );
      const matchesPriority =
        activePriority === 'all' || card.priority === activePriority;
      return matchesQuery && matchesPriority;
    }),
  }));
});

const filteredCardsCount = computed(() =>
  filteredColumns.value.reduce(
    (sum, column) => sum + (column.cards?.length || 0),
    0
  )
);
const productOptions = computed(() => products.value || []);

const allAccountUsers = computed(() => store.getters['agents/getAgents'] || []);

watch(currentBoard, board => {
  columnDraft.board_public_id = board?.public_id || '';
});

watch(boards, boardList => {
  if (!boardList.length) {
    selectedBoardId.value = '';
    return;
  }
  if (!boardList.some(board => board.public_id === selectedBoardId.value)) {
    selectedBoardId.value = boardList[0]?.public_id || '';
  }
});

watch(selectedBoardId, async publicId => {
  store.commit('kanban/SET_ACTIVE_BOARD_ID', publicId || '');
  if (!publicId) return;
  try {
    await store.dispatch('kanban/getBoard', publicId);
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.LOAD_BOARD'));
  }
});

const formatDate = value => {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'short' }).format(
    new Date(value)
  );
};

const formatPhoneNumber = value => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  let local =
    digits.startsWith('55') && digits.length >= 12 ? digits.slice(2) : digits;
  if (local.length === 11)
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10)
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return value;
};

const primaryProduct = card => card.interest_products?.[0];

const openConversation = card => {
  if (!card?.conversation?.id) return;
  window.location.href = `/app/accounts/${window.location.pathname.split('/')[3]}/conversations/${card.conversation.id}`;
};

const visibleCardsForColumn = column => {
  if (!hasActiveCardFilters.value) return column.cards || [];
  return (
    filteredColumns.value.find(item => item.public_id === column.public_id)
      ?.cards || []
  );
};

const isOverWip = column =>
  column.wip_limit != null && (column.cards || []).length >= column.wip_limit;

const isCardVisible = card => {
  const query = cardQuery.value.trim().toLowerCase();
  const matchesQuery =
    !query ||
    card.title?.toLowerCase().includes(query) ||
    card.contact?.name?.toLowerCase().includes(query) ||
    card.interest_products?.some(p =>
      p.product_name?.toLowerCase().includes(query)
    );
  return (
    matchesQuery &&
    (priorityFilter.value === 'all' || card.priority === priorityFilter.value)
  );
};

const openCardDetail = async card => {
  selectedCard.value = card;
  cardDetailTab.value = 'details';
  cardActivities.value = [];
  cardTasks.value = [];
  isCardDetailOpen.value = true;
  try {
    const [activities, tasks] = await Promise.all([
      store.dispatch('kanban/getActivities', card.public_id),
      store.dispatch('kanban/getTasks', card.public_id),
    ]);
    cardActivities.value = activities;
    cardTasks.value = tasks;
  } catch {
    // silently fail, still show the modal
  }
};

const closeCardDetail = () => {
  isCardDetailOpen.value = false;
  selectedCard.value = null;
};

const openCardForm = (card, column) => {
  if (card) {
    selectedCard.value = card;
    Object.assign(cardDraft, {
      title: card.title || '',
      description: card.description || '',
      column_public_id: card.column_public_id || '',
      priority: card.priority || 'normal',
      conversation_display_id: card.conversation?.display_id || '',
      interest_product_public_id:
        card.interest_products?.[0]?.product_public_id || '',
      assignee_id: card.assignee?.id?.toString() || '',
      value_cents: card.value_cents || '',
    });
  } else {
    selectedCard.value = null;
    Object.assign(cardDraft, {
      title: '',
      description: '',
      column_public_id: column?.public_id || '',
      priority: 'normal',
      conversation_display_id: '',
      interest_product_public_id: '',
      assignee_id: '',
      value_cents: '',
    });
  }
  isCardFormOpen.value = true;
};

const closeCardForm = () => {
  isCardFormOpen.value = false;
  if (!isCardDetailOpen.value) selectedCard.value = null;
};

const openCreateBoardModal = () => {
  editingBoard.value = null;
  Object.assign(boardDraft, { name: '', description: '' });
  isBoardModalOpen.value = true;
};

const closeBoardModal = () => {
  isBoardModalOpen.value = false;
  editingBoard.value = null;
  Object.assign(boardDraft, { name: '', description: '' });
};

const openCreateColumnModal = () => {
  editingColumn.value = null;
  Object.assign(columnDraft, {
    name: '',
    color: '#3B82F6',
    board_public_id: currentBoard.value.public_id || '',
    wip_limit: '',
  });
  isColumnModalOpen.value = true;
};

const openEditColumnModal = column => {
  editingColumn.value = column;
  Object.assign(columnDraft, {
    name: column.name || '',
    color: column.color || '#3B82F6',
    board_public_id:
      column.board_public_id || currentBoard.value.public_id || '',
    wip_limit: column.wip_limit != null ? String(column.wip_limit) : '',
  });
  isColumnModalOpen.value = true;
};

const closeColumnModal = () => {
  isColumnModalOpen.value = false;
  editingColumn.value = null;
  Object.assign(columnDraft, {
    name: '',
    color: '#3B82F6',
    board_public_id: currentBoard.value.public_id || '',
    wip_limit: '',
  });
};

const refreshSelectedCard = () => {
  if (!selectedCard.value) return;
  const found = boardColumns.value
    .map(col =>
      col.cards?.find(c => c.public_id === selectedCard.value.public_id)
    )
    .find(Boolean);
  if (found) selectedCard.value = found;
};

const submitCard = async () => {
  const payload = {
    title: cardDraft.title,
    description: cardDraft.description,
    column_public_id: cardDraft.column_public_id,
    priority: cardDraft.priority,
    conversation_display_id: cardDraft.conversation_display_id || undefined,
    interest_product_public_id:
      cardDraft.interest_product_public_id || undefined,
    assignee_id: cardDraft.assignee_id
      ? parseInt(cardDraft.assignee_id, 10)
      : undefined,
    value_cents: cardDraft.value_cents
      ? parseInt(cardDraft.value_cents, 10)
      : undefined,
  };

  try {
    if (selectedCard.value) {
      await store.dispatch('kanban/updateCard', {
        ...payload,
        public_id: selectedCard.value.public_id,
      });
      if (isCardDetailOpen.value) refreshSelectedCard();
    } else {
      await store.dispatch('kanban/createCard', payload);
    }
    closeCardForm();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.CREATE_CARD'));
  }
};

const submitBoard = async () => {
  if (!boardDraft.name.trim()) {
    useAlert(t('KANBAN.ERRORS.BOARD_NAME_REQUIRED'));
    return;
  }
  try {
    const payload = {
      name: boardDraft.name.trim(),
      description: boardDraft.description?.trim(),
    };
    const response = editingBoard.value
      ? await store.dispatch('kanban/updateBoard', {
          public_id: editingBoard.value.public_id,
          ...payload,
        })
      : await store.dispatch('kanban/createBoard', payload);
    selectedBoardId.value = response?.public_id || '';
    closeBoardModal();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.CREATE_BOARD'));
  }
};

const submitColumn = async () => {
  if (!columnDraft.name.trim()) {
    useAlert(t('KANBAN.ERRORS.COLUMN_NAME_REQUIRED'));
    return;
  }
  const boardPublicId =
    columnDraft.board_public_id ||
    selectedBoardId.value ||
    currentBoard.value.public_id;
  if (!boardPublicId) {
    useAlert(t('KANBAN.ERRORS.BOARD_REQUIRED'));
    return;
  }
  try {
    if (editingColumn.value) {
      await store.dispatch('kanban/updateColumn', {
        public_id: editingColumn.value.public_id,
        name: columnDraft.name.trim(),
        color: columnDraft.color,
        board_public_id: boardPublicId,
        wip_limit:
          columnDraft.wip_limit !== ''
            ? parseInt(columnDraft.wip_limit, 10)
            : null,
      });
    } else {
      await store.dispatch('kanban/createColumn', {
        name: columnDraft.name.trim(),
        color: columnDraft.color,
        board_public_id: boardPublicId,
        wip_limit:
          columnDraft.wip_limit !== ''
            ? parseInt(columnDraft.wip_limit, 10)
            : null,
      });
    }
    selectedBoardId.value = boardPublicId;
    closeColumnModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('KANBAN.ERRORS.CREATE_COLUMN')
    );
  }
};

const requestDeleteBoard = () => {
  if (!canDeleteBoard.value) return;
  deleteTarget.value = {
    type: 'board',
    public_id: currentBoard.value.public_id,
    name: currentBoard.value.name,
    title: t('KANBAN.DELETE_BOARD_TITLE'),
    message: t('KANBAN.DELETE_BOARD_MESSAGE'),
    confirmText: t('KANBAN.DELETE_BOARD'),
  };
  showBoardActionsDropdown.value = false;
  isDeleteModalOpen.value = true;
};

const requestDeleteColumn = column => {
  deleteTarget.value = {
    type: 'column',
    public_id: column.public_id,
    name: column.name,
    title: t('KANBAN.DELETE_COLUMN_TITLE'),
    message: t('KANBAN.DELETE_COLUMN_MESSAGE'),
    confirmText: t('KANBAN.DELETE_COLUMN'),
  };
  activeColumnActionsId.value = '';
  isDeleteModalOpen.value = true;
};

const requestDeleteCard = () => {
  if (!selectedCard.value?.public_id) return;
  deleteTarget.value = {
    type: 'card',
    public_id: selectedCard.value.public_id,
    name: selectedCard.value.title,
    title: t('KANBAN.DELETE_CARD_TITLE'),
    message: t('KANBAN.DELETE_CARD_MESSAGE'),
    confirmText: t('KANBAN.DELETE_CARD'),
  };
  isDeleteModalOpen.value = true;
};

const closeDeleteModal = () => {
  isDeleteModalOpen.value = false;
  deleteTarget.value = null;
};

const confirmDelete = async () => {
  try {
    if (deleteTarget.value?.type === 'board') {
      await store.dispatch('kanban/deleteBoard', deleteTarget.value.public_id);
    } else if (deleteTarget.value?.type === 'column') {
      await store.dispatch('kanban/deleteColumn', deleteTarget.value.public_id);
    } else if (deleteTarget.value?.type === 'card') {
      await store.dispatch('kanban/deleteCard', deleteTarget.value.public_id);
      closeCardDetail();
      closeCardForm();
    }
    closeDeleteModal();
  } catch (error) {
    let fallback = t('KANBAN.ERRORS.DELETE_CARD');
    if (deleteTarget.value?.type === 'board')
      fallback = t('KANBAN.ERRORS.DELETE_BOARD');
    else if (deleteTarget.value?.type === 'column')
      fallback = t('KANBAN.ERRORS.DELETE_COLUMN');
    useAlert(error?.response?.data?.message || fallback);
  }
};

const resolveTargetPosition = (column, event, movedCard) => {
  const newVisibleIndex = event.added?.newIndex ?? event.moved?.newIndex ?? 0;
  if (!hasActiveCardFilters.value) return newVisibleIndex;
  const allCards = (column.cards || []).filter(
    card => card.public_id !== movedCard.public_id
  );
  const visibleCards = allCards.filter(card => isCardVisible(card));
  const nextVisible = visibleCards[newVisibleIndex];
  const prevVisible = visibleCards[newVisibleIndex - 1];
  if (nextVisible)
    return allCards.findIndex(card => card.public_id === nextVisible.public_id);
  if (prevVisible)
    return (
      allCards.findIndex(card => card.public_id === prevVisible.public_id) + 1
    );
  return allCards.length;
};

const moveCard = async (column, event) => {
  const moved = event.added?.element || event.moved?.element;
  if (!moved) return;
  try {
    await store.dispatch('kanban/moveCard', {
      public_id: moved.public_id,
      column_public_id: column.public_id,
      position: resolveTargetPosition(column, event, moved),
    });
    if (event.added && isOverWip(column)) {
      useAlert(
        t('KANBAN.WIP_LIMIT_EXCEEDED', {
          column: column.name,
          limit: column.wip_limit,
        })
      );
    }
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.MOVE_CARD'));
    await store.dispatch('kanban/getBoards');
  }
};

const reorderColumns = async () => {
  const payload = boardColumns.value.map((column, index) => ({
    public_id: column.public_id,
    position: index,
  }));
  try {
    await store.dispatch('kanban/reorderColumns', payload);
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('KANBAN.ERRORS.REORDER_COLUMNS')
    );
    await store.dispatch('kanban/getBoards');
  }
};

const columnMenuItems = column => [
  {
    label: t('KANBAN.EDIT_COLUMN'),
    action: 'edit_column',
    value: 'edit_column',
    icon: 'i-lucide-pencil',
    column,
  },
  {
    label: t('KANBAN.DELETE_COLUMN'),
    action: 'delete_column',
    value: 'delete_column',
    icon: 'i-lucide-trash-2',
    column,
  },
];

const handleColumnAction = ({ action, column }) => {
  if (action === 'edit_column') openEditColumnModal(column);
  else if (action === 'delete_column') requestDeleteColumn(column);
};

const postComment = async () => {
  const body = newComment.value.trim();
  if (!body || !selectedCard.value) return;
  isPostingComment.value = true;
  try {
    const activity = await store.dispatch('kanban/createActivity', {
      cardPublicId: selectedCard.value.public_id,
      body,
    });
    cardActivities.value = [activity, ...cardActivities.value];
    newComment.value = '';
  } catch {
    useAlert(t('KANBAN.ERRORS.CREATE_ACTIVITY'));
  } finally {
    isPostingComment.value = false;
  }
};

const addTask = async () => {
  const title = newTaskTitle.value.trim();
  if (!title || !selectedCard.value) return;
  isAddingTask.value = true;
  try {
    const task = await store.dispatch('kanban/createTask', {
      cardPublicId: selectedCard.value.public_id,
      title,
    });
    cardTasks.value = [...cardTasks.value, task];
    newTaskTitle.value = '';
  } catch {
    useAlert(t('KANBAN.ERRORS.CREATE_TASK'));
  } finally {
    isAddingTask.value = false;
  }
};

const toggleTask = async task => {
  try {
    const updated = await store.dispatch('kanban/updateTask', {
      cardPublicId: selectedCard.value.public_id,
      taskPublicId: task.public_id,
      data: { completed: !task.completed },
    });
    const idx = cardTasks.value.findIndex(
      tk => tk.public_id === task.public_id
    );
    if (idx >= 0) cardTasks.value.splice(idx, 1, updated);
  } catch {
    useAlert(t('KANBAN.ERRORS.UPDATE_TASK'));
  }
};

const deleteTask = async task => {
  try {
    await store.dispatch('kanban/deleteTask', {
      cardPublicId: selectedCard.value.public_id,
      taskPublicId: task.public_id,
    });
    cardTasks.value = cardTasks.value.filter(
      tk => tk.public_id !== task.public_id
    );
  } catch {
    useAlert(t('KANBAN.ERRORS.UPDATE_TASK'));
  }
};

const moveCardToColumn = async columnPublicId => {
  if (!selectedCard.value || !columnPublicId) return;
  try {
    await store.dispatch('kanban/moveCard', {
      public_id: selectedCard.value.public_id,
      column_public_id: columnPublicId,
      position: 0,
    });
    selectedCard.value = {
      ...selectedCard.value,
      column_public_id: columnPublicId,
    };
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.MOVE_CARD'));
  }
};

const setupFunnel = async () => {
  isSettingUpFunnel.value = true;
  try {
    const board = await store.dispatch('kanban/setupFunnel');
    selectedBoardId.value = board?.public_id || '';
    useAlert(t('KANBAN.SETUP_FUNNEL_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.CREATE_BOARD'));
  } finally {
    isSettingUpFunnel.value = false;
  }
};

const openBoardSettings = () => {
  if (!currentBoard.value?.public_id) return;
  const entryCol = boardColumns.value.find(c => c.is_default_entry);
  Object.assign(boardSettingsDraft, {
    name: currentBoard.value.name || '',
    description: currentBoard.value.description || '',
    entry_column_public_id: entryCol?.public_id || '',
    is_default: !!currentBoard.value.is_default,
  });
  showBoardActionsDropdown.value = false;
  isBoardSettingsOpen.value = true;
};

const closeBoardSettings = () => {
  isBoardSettingsOpen.value = false;
};

const saveBoardSettings = async () => {
  try {
    await store.dispatch('kanban/updateBoard', {
      public_id: currentBoard.value.public_id,
      name: boardSettingsDraft.name.trim(),
      description: boardSettingsDraft.description?.trim(),
      is_default: boardSettingsDraft.is_default,
    });

    const newEntryId = boardSettingsDraft.entry_column_public_id;
    const oldEntry = boardColumns.value.find(c => c.is_default_entry);
    if (newEntryId && oldEntry?.public_id !== newEntryId) {
      if (oldEntry) {
        await store.dispatch('kanban/updateColumn', {
          public_id: oldEntry.public_id,
          name: oldEntry.name,
          color: oldEntry.color,
          board_public_id: currentBoard.value.public_id,
          is_default_entry: false,
        });
      }
      await store.dispatch('kanban/updateColumn', {
        public_id: newEntryId,
        name:
          boardColumns.value.find(c => c.public_id === newEntryId)?.name || '',
        color:
          boardColumns.value.find(c => c.public_id === newEntryId)?.color ||
          '#3B82F6',
        board_public_id: currentBoard.value.public_id,
        is_default_entry: true,
      });
    }

    useAlert(t('KANBAN.SETTINGS_SAVED'));
    closeBoardSettings();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.CREATE_BOARD'));
  }
};

const migratableColumns = computed(() =>
  boardColumns.value.filter(col => (col.cards || []).length > 0)
);

const migrateTargetBoard = computed(
  () =>
    boards.value.find(b => b.public_id === migrateTargetBoardId.value) || null
);

const openMigrateModal = () => {
  migrateTargetBoardId.value = '';
  migrateStep.value = 1;
  columnMappings.value = {};
  showBoardActionsDropdown.value = false;
  isMigrateModalOpen.value = true;
};

const advanceMigrateStep = () => {
  if (!migrateTargetBoardId.value) {
    useAlert(t('KANBAN.MIGRATE_NO_TARGET'));
    return;
  }
  const init = {};
  migratableColumns.value.forEach(col => {
    init[col.public_id] = { destination: '', inherit: true };
  });
  columnMappings.value = init;
  migrateStep.value = 2;
};

const migrateBoard = async () => {
  const mappings = Object.entries(columnMappings.value).map(
    ([sourceColPublicId, v]) => ({
      source_column_public_id: sourceColPublicId,
      destination: v.destination,
      inherit: v.inherit,
    })
  );
  const hasAnyMigration = mappings.some(m => m.destination !== '');
  if (!hasAnyMigration) {
    useAlert(t('KANBAN.MIGRATE_NO_MAPPING'));
    return;
  }
  isMigrating.value = true;
  try {
    const result = await store.dispatch('kanban/migrateBoard', {
      sourceBoardPublicId: currentBoard.value.public_id,
      target_board_public_id: migrateTargetBoardId.value,
      column_mappings: mappings,
    });
    useAlert(t('KANBAN.MIGRATE_SUCCESS', { count: result.count }));
    isMigrateModalOpen.value = false;
    await store.dispatch('kanban/getBoard', selectedBoardId.value);
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('KANBAN.ERRORS.MIGRATE_BOARD')
    );
  } finally {
    isMigrating.value = false;
  }
};

const updateCardAssignee = async assigneeId => {
  if (!selectedCard.value) return;
  try {
    await store.dispatch('kanban/updateCard', {
      public_id: selectedCard.value.public_id,
      assignee_id: assigneeId ? parseInt(assigneeId, 10) : null,
    });
    await store.dispatch('kanban/getBoard', selectedBoardId.value);
    refreshSelectedCard();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.CREATE_CARD'));
  }
};

const updateCardProduct = async productPublicId => {
  if (!selectedCard.value) return;
  try {
    await store.dispatch('kanban/updateCard', {
      public_id: selectedCard.value.public_id,
      interest_product_public_id: productPublicId || undefined,
    });
    await store.dispatch('kanban/getBoard', selectedBoardId.value);
    refreshSelectedCard();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KANBAN.ERRORS.CREATE_CARD'));
  }
};

onMounted(async () => {
  try {
    await Promise.all([
      store.dispatch('kanban/getBoards'),
      store.dispatch('products/getProducts'),
      store.dispatch('agents/get'),
    ]);
  } catch (error) {
    useAlert(
      error?.response?.data?.message || t('KANBAN.ERRORS.LOAD_PRODUCTS')
    );
  }
  selectedBoardId.value = boards.value[0]?.public_id || '';
  columnDraft.board_public_id = currentBoard.value.public_id || '';
});

function columnTintStyle(column) {
  const c = column.color || '#3b82f6';
  return {
    backgroundImage: `linear-gradient(180deg, color-mix(in srgb, ${c} 8%, transparent) 0%, transparent 35%)`,
  };
}

function columnGlowBarStyle(column) {
  const c = column.color || '#3b82f6';
  return {
    background: c,
    boxShadow: `0 0 18px ${c}, 0 0 44px color-mix(in srgb, ${c} 55%, transparent)`,
  };
}

function columnBlobStyle(column) {
  const c = column.color || '#3b82f6';
  return {
    background: `radial-gradient(circle, color-mix(in srgb, ${c} 14%, transparent), transparent 65%)`,
    filter: 'blur(20px)',
  };
}

function columnNumberStyle(column) {
  const c = column.color || '#3b82f6';
  return {
    color: c,
    textShadow: `0 0 18px color-mix(in srgb, ${c} 60%, transparent)`,
  };
}

function cardTintStyle(column) {
  const c = column.color || '#3b82f6';
  return {
    backgroundImage: `radial-gradient(circle at 100% 0%, color-mix(in srgb, ${c} 9%, transparent), transparent 55%)`,
  };
}

function addCardBtnStyle(column) {
  const c = column.color || '#3b82f6';
  return {
    borderColor: `color-mix(in srgb, ${c} 55%, transparent)`,
    color: c,
  };
}
</script>

<template>
  <div
    class="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden bg-n-surface-1"
  >
    <!-- Header -->
    <div class="border-b border-n-weak px-6 py-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('KANBAN.TITLE') }}
          </h1>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            color="slate"
            size="sm"
            variant="outline"
            :label="t('KANBAN.VIEW_SUMMARY')"
            @click="isSummaryModalOpen = true"
          />
          <Button
            color="teal"
            size="sm"
            variant="outline"
            icon="i-lucide-zap"
            :label="t('KANBAN.SETUP_FUNNEL')"
            :disabled="isSettingUpFunnel"
            @click="setupFunnel"
          />
          <Button
            color="blue"
            size="sm"
            :label="t('KANBAN.NEW_BOARD')"
            @click="openCreateBoardModal"
          />
          <Button
            color="slate"
            size="sm"
            variant="outline"
            :label="t('KANBAN.NEW_COLUMN')"
            @click="openCreateColumnModal"
          />
          <Button
            v-if="currentBoard?.public_id"
            icon="i-lucide-arrow-right-left"
            color="slate"
            variant="ghost"
            size="sm"
            :title="t('KANBAN.MIGRATE_BOARD_BUTTON')"
            @click="openMigrateModal"
          />
          <Button
            v-if="currentBoard?.public_id"
            icon="i-lucide-settings-2"
            color="slate"
            variant="ghost"
            size="sm"
            @click="openBoardSettings"
          />
          <div
            v-if="currentBoard?.public_id && canDeleteBoard"
            v-on-clickaway="() => (showBoardActionsDropdown = false)"
            class="relative"
          >
            <Button
              icon="i-lucide-trash-2"
              color="ruby"
              variant="ghost"
              size="sm"
              @click="requestDeleteBoard"
            />
          </div>
        </div>
      </div>

      <div
        class="mt-5 grid gap-3 md:grid-cols-2 2xl:grid-cols-[18rem_minmax(0,1fr)_15rem_auto]"
      >
        <select
          v-model="selectedBoardId"
          class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <option
            v-for="board in boards"
            :key="board.public_id"
            :value="board.public_id"
          >
            {{ board.name }}
          </option>
        </select>
        <input
          v-model="cardQuery"
          type="search"
          :placeholder="t('KANBAN.SEARCH_PLACEHOLDER')"
          class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        />
        <select
          v-model="priorityFilter"
          class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
        >
          <option value="all">{{ t('KANBAN.ALL_PRIORITIES') }}</option>
          <option value="low">{{ t('KANBAN.PRIORITY.low') }}</option>
          <option value="normal">{{ t('KANBAN.PRIORITY.normal') }}</option>
          <option value="high">{{ t('KANBAN.PRIORITY.high') }}</option>
          <option value="urgent">{{ t('KANBAN.PRIORITY.urgent') }}</option>
        </select>
        <div
          class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-11 md:col-span-2 2xl:col-span-1"
        >
          <span v-if="selectedBoardName">
            {{ selectedBoardName }} · {{ filteredCardsCount }}
            {{ t('KANBAN.FILTERED_CARDS') }}
          </span>
          <span v-else>
            {{ filteredCardsCount }} {{ t('KANBAN.FILTERED_CARDS') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Loading / Empty states -->
    <div
      v-if="uiFlags.isFetching || uiFlags.isFetchingBoard"
      class="p-6 text-n-slate-11"
    >
      {{ t('KANBAN.LOADING') }}
    </div>

    <div v-else-if="!boards.length" class="flex-1 overflow-auto p-6">
      <div class="flex min-h-full">
        <div
          class="flex min-h-[26rem] w-full flex-col justify-between rounded-3xl border border-dashed border-n-weak bg-n-alpha-1 p-8 text-n-slate-11"
        >
          <div class="max-w-3xl">
            <h2 class="text-2xl font-semibold text-n-slate-12 sm:text-3xl">
              {{ t('KANBAN.EMPTY') }}
            </h2>
            <p class="mt-3 max-w-2xl text-base leading-7">
              {{ t('KANBAN.EMPTY_DESCRIPTION') }}
            </p>
            <div class="mt-8 flex flex-wrap gap-3">
              <Button
                color="blue"
                :label="t('KANBAN.NEW_BOARD')"
                @click="openCreateBoardModal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="!boardColumns.length" class="flex-1 overflow-auto p-6">
      <div class="flex min-h-full">
        <div
          class="flex min-h-[26rem] w-full flex-col justify-between rounded-3xl border border-dashed border-n-weak bg-n-alpha-1 p-8 text-n-slate-11"
        >
          <div class="max-w-3xl">
            <h2 class="text-2xl font-semibold text-n-slate-12 sm:text-3xl">
              {{ t('KANBAN.EMPTY_COLUMNS') }}
            </h2>
            <p class="mt-3 max-w-2xl text-base leading-7">
              {{ t('KANBAN.EMPTY_COLUMNS_DESCRIPTION') }}
            </p>
            <div class="mt-8 flex flex-wrap gap-3">
              <Button
                color="blue"
                :label="t('KANBAN.NEW_COLUMN')"
                @click="openCreateColumnModal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Board columns -->
    <div
      v-else
      class="flex-1 overflow-x-auto overflow-y-hidden px-[34px] pb-[30px] pt-5"
    >
      <Draggable
        :list="boardColumns"
        item-key="public_id"
        class="flex h-full min-w-max items-stretch gap-4"
        handle=".kanban-column-handle"
        @end="reorderColumns"
      >
        <template #item="{ element: column, index }">
          <section
            class="relative flex w-[300px] shrink-0 flex-col rounded-2xl border border-n-weak bg-n-solid-1 2xl:w-[320px]"
            :class="{ 'opacity-80': hasActiveCardFilters }"
            :style="columnTintStyle(column)"
          >
            <!-- Visual decorations layer (clipped, doesn't interfere with drag) -->
            <div
              class="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl"
            >
              <!-- Glow accent bar -->
              <div
                class="absolute left-0 right-0 top-0 h-[3px] opacity-95"
                :style="columnGlowBarStyle(column)"
              />
              <!-- Radial color blob -->
              <div
                class="absolute left-[-20%] top-[-14%] h-[38%] w-4/5 opacity-70"
                :style="columnBlobStyle(column)"
              />
            </div>

            <!-- Column header -->
            <div
              class="relative z-20 grid grid-cols-[1fr_auto] items-start gap-3 px-5 pb-4 pt-5"
            >
              <div>
                <div class="flex min-w-0 items-center gap-3">
                  <button
                    type="button"
                    class="kanban-column-handle shrink-0 cursor-grab"
                  >
                    <span
                      class="text-[15px] font-bold leading-none"
                      :style="columnNumberStyle(column)"
                      >{{ index + 1 }}</span
                    >
                  </button>
                  <h2
                    class="truncate text-base font-bold tracking-[-0.015em] text-n-slate-12"
                  >
                    {{ column.name }}
                  </h2>
                </div>
                <p
                  class="ml-[27px] mt-1 text-[13px]"
                  :class="
                    isOverWip(column)
                      ? 'font-semibold text-ruby-600 dark:text-ruby-400'
                      : 'text-n-slate-11'
                  "
                >
                  {{
                    column.wip_limit
                      ? `${(column.cards || []).length} / ${column.wip_limit}`
                      : (column.cards || []).length
                  }}
                  {{ t('KANBAN.CARDS_COUNT') }}
                </p>
              </div>
              <div
                v-on-clickaway="
                  () =>
                    activeColumnActionsId === column.public_id &&
                    (activeColumnActionsId = '')
                "
                class="relative"
              >
                <Button
                  icon="i-lucide-ellipsis-vertical"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  :class="
                    activeColumnActionsId === column.public_id
                      ? 'bg-n-alpha-2'
                      : ''
                  "
                  @click="
                    activeColumnActionsId =
                      activeColumnActionsId === column.public_id
                        ? ''
                        : column.public_id
                  "
                />
                <DropdownMenu
                  v-if="activeColumnActionsId === column.public_id"
                  :menu-items="columnMenuItems(column)"
                  class="right-0 top-full mt-1 w-52"
                  @action="handleColumnAction"
                />
              </div>
            </div>

            <!-- Cards scrollable area -->
            <div class="relative z-10 min-h-0 flex-1 overflow-y-auto px-3 pb-2">
              <Draggable
                :list="column.cards"
                item-key="public_id"
                group="kanban-cards"
                class="flex min-h-12 flex-col gap-3"
                @change="moveCard(column, $event)"
              >
                <template #item="{ element }">
                  <article
                    v-show="isCardVisible(element)"
                    class="relative cursor-pointer rounded-xl border border-n-weak bg-n-solid-2 p-4 transition-colors hover:border-n-slate-6"
                    :style="cardTintStyle(column)"
                    @click="openCardDetail(element)"
                  >
                    <!-- Name + priority badge -->
                    <div
                      class="mb-3 grid grid-cols-[1fr_auto] items-start gap-2"
                    >
                      <h3
                        class="text-sm font-semibold leading-snug tracking-[-0.01em] text-n-slate-12"
                      >
                        {{ element.contact?.name || element.title }}
                      </h3>
                      <KanbanPriorityBadge
                        :priority="element.priority"
                        :label="t(`KANBAN.PRIORITY.${element.priority}`)"
                      />
                    </div>

                    <!-- Meta grid: product/price + assignee/date -->
                    <div
                      class="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-2"
                    >
                      <div
                        v-if="primaryProduct(element)"
                        class="flex min-w-0 items-center gap-2 text-xs text-n-slate-11"
                      >
                        <span
                          class="i-lucide-package h-4 w-4 shrink-0 text-n-slate-10"
                        />
                        <span class="truncate">{{
                          primaryProduct(element).product_name
                        }}</span>
                      </div>
                      <div
                        v-if="primaryProduct(element)?.unit_price_cents"
                        class="whitespace-nowrap text-right text-[13px] font-semibold text-n-slate-12"
                      >
                        {{
                          (
                            primaryProduct(element).unit_price_cents / 100
                          ).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: 'BRL',
                          })
                        }}
                      </div>
                      <div
                        class="flex min-w-0 items-center gap-2 text-xs text-n-slate-11"
                      >
                        <div
                          v-if="element.assignee"
                          class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-400 to-slate-600 text-[10px] font-bold text-white"
                        >
                          {{ element.assignee.name?.charAt(0)?.toUpperCase() }}
                        </div>
                        <span
                          v-else
                          class="i-lucide-user-round h-4 w-4 shrink-0 text-n-slate-10"
                        />
                        <span class="truncate">{{
                          element.assignee?.name || t('KANBAN.NO_ASSIGNEE')
                        }}</span>
                      </div>
                      <div
                        v-if="element.last_conversation_at"
                        class="flex items-center justify-end gap-1 whitespace-nowrap text-xs text-n-slate-11"
                      >
                        <span
                          class="i-lucide-calendar h-3.5 w-3.5 text-n-slate-10"
                        />
                        {{ formatDate(element.last_conversation_at) }}
                      </div>
                    </div>

                    <!-- Open conversation button -->
                    <button
                      v-if="element.conversation?.id"
                      type="button"
                      class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-xs font-medium text-n-slate-12 transition-colors hover:border-n-slate-6 hover:bg-n-alpha-2"
                      @click.stop="openConversation(element)"
                    >
                      <span class="i-lucide-message-circle h-3.5 w-3.5" />
                      {{ t('KANBAN.OPEN_CONVERSATION') }}
                    </button>
                  </article>
                </template>
              </Draggable>

              <!-- Per-column empty state -->
              <div
                v-if="!column.cards?.length"
                class="flex flex-col items-center justify-center gap-3 py-8 text-center"
              >
                <div
                  class="flex h-14 w-14 items-center justify-center rounded-full border border-dashed border-n-weak"
                  :style="{ color: column.color }"
                >
                  <span class="i-lucide-inbox h-6 w-6" />
                </div>
                <div>
                  <p class="text-sm font-semibold text-n-slate-12">
                    {{ t('KANBAN.EMPTY_COLUMN_CARDS') }}
                  </p>
                  <p
                    class="mx-auto mt-1 max-w-[200px] text-xs leading-relaxed text-n-slate-11"
                  >
                    {{ t('KANBAN.EMPTY_COLUMN_CARDS_DESC') }}
                  </p>
                </div>
              </div>

              <!-- No filter match -->
              <div
                v-if="
                  hasActiveCardFilters && !visibleCardsForColumn(column).length
                "
                class="mt-3 rounded-xl border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-11"
              >
                {{ t('KANBAN.NO_MATCHING_CARDS') }}
              </div>
            </div>

            <!-- Add card button (pinned bottom) -->
            <div class="relative z-10 shrink-0 px-3 pb-4 pt-2">
              <button
                class="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-dashed bg-transparent text-sm font-medium transition-colors hover:bg-n-alpha-1"
                :style="addCardBtnStyle(column)"
                @click="openCardForm(null, column)"
              >
                <span class="i-lucide-plus h-4 w-4" />
                {{ t('KANBAN.ADD_CARD') }}
              </button>
            </div>
          </section>
        </template>
      </Draggable>
    </div>

    <!-- Card detail modal -->
    <Modal v-model:show="isCardDetailOpen" :on-close="closeCardDetail">
      <div v-if="selectedCard" class="w-full max-w-2xl">
        <!-- Modal header -->
        <div
          class="flex items-start justify-between gap-4 border-b border-n-weak pl-6 pr-14 py-5"
        >
          <div class="min-w-0">
            <h2 class="text-lg font-semibold text-n-slate-12 leading-snug">
              {{ selectedCard.contact?.name || selectedCard.title }}
            </h2>
            <p
              v-if="
                selectedCard.contact?.name &&
                selectedCard.title !== selectedCard.contact.name
              "
              class="mt-0.5 text-sm text-n-slate-11"
            >
              {{ selectedCard.title }}
            </p>
          </div>
          <div class="flex shrink-0 gap-2">
            <button
              v-if="selectedCard.conversation?.id"
              class="flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
              @click="openConversation(selectedCard)"
            >
              <span class="i-lucide-message-circle h-3.5 w-3.5" />
              {{ t('KANBAN.OPEN_CONVERSATION') }}
            </button>
            <Button
              icon="i-lucide-pencil"
              color="slate"
              variant="ghost"
              size="sm"
              @click="openCardForm(selectedCard, null)"
            />
            <Button
              icon="i-lucide-trash-2"
              color="ruby"
              variant="ghost"
              size="sm"
              @click="requestDeleteCard"
            />
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex border-b border-n-weak px-6">
          <button
            v-for="tab in ['details', 'tasks', 'comments', 'activity']"
            :key="tab"
            class="border-b-2 px-4 py-3 text-sm font-medium transition-colors"
            :class="
              cardDetailTab === tab
                ? 'border-n-brand text-n-brand'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="cardDetailTab = tab"
          >
            <span v-if="tab === 'details'">{{
              t('KANBAN.DETAILS_SECTION')
            }}</span>
            <span v-else-if="tab === 'tasks'"
              >{{ t('KANBAN.TASKS_SECTION') }}
              <span
                v-if="cardTasks.length"
                class="ml-1 rounded-full bg-n-alpha-2 px-1.5 text-xs"
                >{{ cardTasks.length }}</span
              ></span
            >
            <span v-else-if="tab === 'comments'">{{
              t('KANBAN.COMMENTS_SECTION')
            }}</span>
            <span v-else>{{ t('KANBAN.ACTIVITY_SECTION') }}</span>
          </button>
        </div>

        <div class="max-h-[60vh] overflow-y-auto px-6 py-5">
          <!-- Details tab -->
          <div
            v-if="cardDetailTab === 'details'"
            class="grid gap-4 sm:grid-cols-2"
          >
            <div v-if="selectedCard.contact?.name">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.PHONE_NUMBER') }}
              </p>
              <p class="mt-1 text-sm text-n-slate-12">
                {{
                  formatPhoneNumber(selectedCard.phone_number) ||
                  selectedCard.contact?.email ||
                  '—'
                }}
              </p>
            </div>
            <div>
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.ASSIGNEE_LABEL') }}
              </p>
              <select
                :value="selectedCard.assignee?.id?.toString() || ''"
                class="mt-1 w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
                @change="updateCardAssignee($event.target.value)"
              >
                <option value="">{{ t('KANBAN.NO_ASSIGNEE') }}</option>
                <option
                  v-for="user in allAccountUsers"
                  :key="user.id"
                  :value="user.id.toString()"
                >
                  {{ user.name }}
                </option>
              </select>
            </div>
            <div>
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.FIRST_CONTACT') }}
              </p>
              <p class="mt-1 text-sm text-n-slate-12">
                {{ formatDate(selectedCard.created_at) }}
              </p>
            </div>
            <div>
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.LAST_CONTACT') }}
              </p>
              <p class="mt-1 text-sm text-n-slate-12">
                {{ formatDate(selectedCard.last_conversation_at) || '—' }}
              </p>
            </div>
            <div class="sm:col-span-2">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.INTEREST_PRODUCT') }}
              </p>
              <select
                :value="primaryProduct(selectedCard)?.product_public_id || ''"
                class="mt-1 w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
                @change="updateCardProduct($event.target.value)"
              >
                <option value="">{{ t('KANBAN.SELECT_PRODUCT') }}</option>
                <option
                  v-for="product in productOptions"
                  :key="product.id"
                  :value="product.id"
                >
                  {{ product.attributes.name }}
                </option>
              </select>
            </div>
            <div v-if="selectedCard.value_cents">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.VALUE_LABEL') }}
              </p>
              <p class="mt-1 text-sm font-semibold text-n-slate-12">
                {{
                  (selectedCard.value_cents / 100).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })
                }}
              </p>
            </div>
            <div class="sm:col-span-2">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.MOVE_TO_COLUMN') }}
              </p>
              <select
                :value="selectedCard.column_public_id"
                class="mt-1 w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
                @change="moveCardToColumn($event.target.value)"
              >
                <option
                  v-for="col in boardColumns"
                  :key="col.public_id"
                  :value="col.public_id"
                >
                  {{ col.name }}
                </option>
              </select>
            </div>
            <div v-if="selectedCard.description" class="sm:col-span-2">
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('KANBAN.CARD_DESCRIPTION') }}
              </p>
              <p class="mt-1 text-sm text-n-slate-12 whitespace-pre-wrap">
                {{ selectedCard.description }}
              </p>
            </div>
          </div>

          <!-- Tasks tab -->
          <div v-else-if="cardDetailTab === 'tasks'">
            <div class="mb-4 flex gap-2">
              <input
                v-model="newTaskTitle"
                :placeholder="t('KANBAN.TASK_PLACEHOLDER')"
                class="flex-1 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                @keydown.enter="addTask"
              />
              <Button
                color="blue"
                size="sm"
                :label="t('KANBAN.ADD_TASK')"
                :disabled="isAddingTask"
                @click="addTask"
              />
            </div>
            <div
              v-if="!cardTasks.length"
              class="text-center text-sm text-n-slate-11 py-6"
            >
              {{ t('KANBAN.NO_TASKS') }}
            </div>
            <ul v-else class="space-y-2">
              <li
                v-for="task in cardTasks"
                :key="task.public_id"
                class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
              >
                <input
                  type="checkbox"
                  :checked="task.completed"
                  class="h-4 w-4 shrink-0 cursor-pointer accent-n-brand"
                  @change="toggleTask(task)"
                />
                <span
                  class="flex-1 text-sm text-n-slate-12"
                  :class="{ 'line-through text-n-slate-10': task.completed }"
                  >{{ task.title }}</span
                >
                <button
                  class="text-n-slate-10 hover:text-red-500"
                  @click="deleteTask(task)"
                >
                  <span class="i-lucide-x h-4 w-4" />
                </button>
              </li>
            </ul>
          </div>

          <!-- Comments tab -->
          <div v-else-if="cardDetailTab === 'comments'">
            <div class="mb-4">
              <textarea
                v-model="newComment"
                rows="3"
                :placeholder="t('KANBAN.COMMENT_PLACEHOLDER')"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              />
              <div class="mt-2 flex justify-end">
                <Button
                  color="blue"
                  size="sm"
                  :label="t('KANBAN.SEND')"
                  :disabled="isPostingComment || !newComment.trim()"
                  @click="postComment"
                />
              </div>
            </div>
            <div
              v-if="
                !cardActivities.filter(a => a.action === 'commented').length
              "
              class="text-center text-sm text-n-slate-11 py-6"
            >
              {{ t('KANBAN.NO_COMMENTS') }}
            </div>
            <ul v-else class="space-y-3">
              <li
                v-for="activity in cardActivities.filter(
                  a => a.action === 'commented'
                )"
                :key="activity.public_id"
                class="rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3"
              >
                <div
                  class="flex items-center justify-between gap-2 text-xs text-n-slate-10 mb-1.5"
                >
                  <span class="font-medium text-n-slate-11">{{
                    activity.user?.name || '—'
                  }}</span>
                  <span>{{ formatDate(activity.created_at) }}</span>
                </div>
                <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
                  {{ activity.metadata?.body }}
                </p>
              </li>
            </ul>
          </div>

          <!-- Activity log tab -->
          <div v-else>
            <ul v-if="cardActivities.length" class="space-y-2">
              <li
                v-for="activity in cardActivities"
                :key="activity.public_id"
                class="flex items-start gap-3 text-sm text-n-slate-11"
              >
                <span
                  class="i-lucide-activity mt-0.5 h-4 w-4 shrink-0 text-n-slate-9"
                />
                <div class="flex-1">
                  <span class="font-medium text-n-slate-12">{{
                    activity.user?.name || '—'
                  }}</span>
                  <span v-if="activity.action === 'commented'" class="ml-1">
                    {{ t('KANBAN.ACTIVITY_COMMENTED') }}:
                    <em>{{ activity.metadata?.body }}</em></span
                  >
                  <span v-else-if="activity.action === 'moved'" class="ml-1">
                    {{ t('KANBAN.ACTIVITY_MOVED_TO') }}
                    <strong>{{ activity.to_column?.name }}</strong></span
                  >
                  <span
                    v-else-if="activity.action === 'assigned'"
                    class="ml-1"
                    >{{ t('KANBAN.ACTIVITY_ASSIGNED') }}</span>
                  <span
                    v-else-if="activity.action === 'created'"
                    class="ml-1"
                    >{{ t('KANBAN.ACTIVITY_CREATED') }}</span>
                  <p class="text-xs text-n-slate-9 mt-0.5">
                    {{ formatDate(activity.created_at) }}
                  </p>
                </div>
              </li>
            </ul>
            <div v-else class="py-6 text-center text-sm text-n-slate-11">
              {{ t('KANBAN.NO_ACTIVITY') }}
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Card create/edit form modal -->
    <Modal v-model:show="isCardFormOpen" :on-close="closeCardForm">
      <div class="w-full max-w-[42rem] p-6">
        <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
          {{ selectedCard ? t('KANBAN.EDIT_CARD') : t('KANBAN.CREATE_CARD') }}
        </h2>
        <form class="space-y-4" @submit.prevent="submitCard">
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.CARD_TITLE')
            }}</label>
            <input
              v-model="cardDraft.title"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
              required
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.CARD_DESCRIPTION')
            }}</label>
            <textarea
              v-model="cardDraft.description"
              rows="3"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
            />
          </div>
          <div class="grid gap-4 sm:grid-cols-2">
            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
                t('KANBAN.COLUMN_LABEL')
              }}</label>
              <select
                v-model="cardDraft.column_public_id"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
              >
                <option
                  v-for="col in boardColumns"
                  :key="col.public_id"
                  :value="col.public_id"
                >
                  {{ col.name }}
                </option>
              </select>
            </div>
            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
                t('KANBAN.PRIORITY_LABEL')
              }}</label>
              <select
                v-model="cardDraft.priority"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
              >
                <option value="low">{{ t('KANBAN.PRIORITY.low') }}</option>
                <option value="normal">
                  {{ t('KANBAN.PRIORITY.normal') }}
                </option>
                <option value="high">{{ t('KANBAN.PRIORITY.high') }}</option>
                <option value="urgent">
                  {{ t('KANBAN.PRIORITY.urgent') }}
                </option>
              </select>
            </div>
            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
                t('KANBAN.CONVERSATION_DISPLAY_ID')
              }}</label>
              <input
                v-model="cardDraft.conversation_display_id"
                type="number"
                min="1"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
                :placeholder="t('KANBAN.CONVERSATION_DISPLAY_ID_PLACEHOLDER')"
              />
            </div>
            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
                t('KANBAN.ASSIGNEE_LABEL')
              }}</label>
              <select
                v-model="cardDraft.assignee_id"
                class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
              >
                <option value="">{{ t('KANBAN.NO_ASSIGNEE') }}</option>
                <option
                  v-for="user in allAccountUsers"
                  :key="user.id"
                  :value="user.id.toString()"
                >
                  {{ user.name }}
                </option>
              </select>
            </div>
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.INTEREST_PRODUCT')
            }}</label>
            <select
              v-model="cardDraft.interest_product_public_id"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
            >
              <option value="">{{ t('KANBAN.SELECT_PRODUCT') }}</option>
              <option
                v-for="product in productOptions"
                :key="product.id"
                :value="product.id"
              >
                {{ product.attributes.name }}
              </option>
            </select>
          </div>
          <div class="flex justify-between gap-2">
            <Button
              v-if="selectedCard"
              type="button"
              variant="outline"
              color="ruby"
              :label="t('KANBAN.DELETE_CARD')"
              @click="requestDeleteCard"
            />
            <div class="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                color="slate"
                :label="t('KANBAN.CANCEL')"
                @click="closeCardForm"
              />
              <Button
                type="submit"
                color="blue"
                :label="
                  selectedCard ? t('KANBAN.SAVE') : t('KANBAN.CREATE_CARD')
                "
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>

    <!-- Board modal -->
    <Modal v-model:show="isBoardModalOpen" :on-close="closeBoardModal">
      <div class="w-full max-w-[36rem] p-6">
        <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
          {{ editingBoard ? t('KANBAN.EDIT_BOARD') : t('KANBAN.NEW_BOARD') }}
        </h2>
        <form class="space-y-4" @submit.prevent="submitBoard">
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.BOARD_NAME')
            }}</label>
            <input
              v-model="boardDraft.name"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
              required
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.BOARD_DESCRIPTION')
            }}</label>
            <textarea
              v-model="boardDraft.description"
              rows="3"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
            />
          </div>
          <div class="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              color="slate"
              :label="t('KANBAN.CANCEL')"
              @click="closeBoardModal"
            />
            <Button
              type="button"
              color="blue"
              :label="
                editingBoard ? t('KANBAN.SAVE') : t('KANBAN.CREATE_BOARD')
              "
              @click="submitBoard"
            />
          </div>
        </form>
      </div>
    </Modal>

    <!-- Column modal -->
    <Modal v-model:show="isColumnModalOpen" :on-close="closeColumnModal">
      <div class="w-full max-w-[36rem] p-6">
        <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
          {{ editingColumn ? t('KANBAN.EDIT_COLUMN') : t('KANBAN.NEW_COLUMN') }}
        </h2>
        <form class="space-y-4" @submit.prevent="submitColumn">
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.COLUMN_NAME')
            }}</label>
            <input
              v-model="columnDraft.name"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
              required
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.COLUMN_COLOR')
            }}</label>
            <input
              v-model="columnDraft.color"
              type="color"
              class="h-11 w-full rounded-lg border border-n-weak bg-n-surface-1 px-2 py-2 outline-none focus:border-n-brand"
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.WIP_LIMIT_LABEL')
            }}</label>
            <input
              v-model="columnDraft.wip_limit"
              type="number"
              min="1"
              :placeholder="t('KANBAN.WIP_LIMIT_PLACEHOLDER')"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 outline-none focus:border-n-brand"
            />
          </div>
          <div class="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              color="slate"
              :label="t('KANBAN.CANCEL')"
              @click="closeColumnModal"
            />
            <Button
              type="button"
              color="blue"
              :label="
                editingColumn ? t('KANBAN.SAVE') : t('KANBAN.CREATE_COLUMN')
              "
              @click="submitColumn"
            />
          </div>
        </form>
      </div>
    </Modal>

    <!-- Summary modal -->
    <Modal
      v-model:show="isSummaryModalOpen"
      :on-close="() => (isSummaryModalOpen = false)"
    >
      <div class="w-full max-w-[36rem] p-6">
        <h2 class="mb-4 text-lg font-semibold text-n-slate-12">
          {{ t('KANBAN.SUMMARY_TITLE') }}
        </h2>
        <div class="grid gap-4 md:grid-cols-3">
          <div class="rounded-3xl border border-n-weak bg-n-alpha-1 p-5">
            <p class="text-xs uppercase tracking-wide text-n-slate-10">
              {{ t('KANBAN.STATS.BOARDS') }}
            </p>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ boards.length }}
            </p>
          </div>
          <div class="rounded-3xl border border-n-weak bg-n-alpha-1 p-5">
            <p class="text-xs uppercase tracking-wide text-n-slate-10">
              {{ t('KANBAN.STATS.COLUMNS') }}
            </p>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ totalColumns }}
            </p>
          </div>
          <div class="rounded-3xl border border-n-weak bg-n-alpha-1 p-5">
            <p class="text-xs uppercase tracking-wide text-n-slate-10">
              {{ t('KANBAN.STATS.CARDS') }}
            </p>
            <p class="mt-3 text-3xl font-semibold text-n-slate-12">
              {{ totalCards }}
            </p>
            <p class="mt-3 text-sm text-n-slate-11">
              {{ filteredCardsCount }} {{ t('KANBAN.FILTERED_CARDS') }}
            </p>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Board settings modal -->
    <Modal v-model:show="isBoardSettingsOpen" :on-close="closeBoardSettings">
      <div class="w-full max-w-[36rem] p-6">
        <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
          {{ t('KANBAN.BOARD_SETTINGS') }}
        </h2>
        <p class="mb-5 text-sm text-n-slate-11">
          {{ currentBoard.name }}
        </p>
        <form class="space-y-5" @submit.prevent="saveBoardSettings">
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.BOARD_NAME')
            }}</label>
            <input
              v-model="boardSettingsDraft.name"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm outline-none focus:border-n-brand"
              required
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.BOARD_DESCRIPTION')
            }}</label>
            <textarea
              v-model="boardSettingsDraft.description"
              rows="2"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm outline-none focus:border-n-brand"
            />
          </div>
          <label
            class="flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors"
            :class="
              boardSettingsDraft.is_default
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-brand/40'
            "
          >
            <input
              v-model="boardSettingsDraft.is_default"
              type="checkbox"
              class="mt-0.5 accent-n-brand"
            />
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('KANBAN.LEAD_ENTRY_TOGGLE') }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ t('KANBAN.LEAD_ENTRY_TOGGLE_DESCRIPTION') }}
              </p>
            </div>
          </label>

          <div v-if="boardSettingsDraft.is_default">
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.ENTRY_COLUMN_LABEL')
            }}</label>
            <p class="mb-2 text-xs text-n-slate-10">
              {{ t('KANBAN.ENTRY_COLUMN_DESCRIPTION') }}
            </p>
            <div class="space-y-2">
              <label
                v-for="col in boardColumns"
                :key="col.public_id"
                class="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors"
                :class="
                  boardSettingsDraft.entry_column_public_id === col.public_id
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak hover:border-n-brand/40'
                "
              >
                <input
                  v-model="boardSettingsDraft.entry_column_public_id"
                  type="radio"
                  :value="col.public_id"
                  class="accent-n-brand"
                />
                <span
                  class="h-2.5 w-2.5 shrink-0 rounded-full"
                  :style="{ background: col.color || '#3B82F6' }"
                />
                <span class="text-sm text-n-slate-12">{{ col.name }}</span>
                <span
                  v-if="col.is_default_entry"
                  class="ml-auto text-xs text-n-slate-10"
                  >{{ t('KANBAN.ENTRY_COLUMN_CURRENT') }}</span
                >
              </label>
            </div>
          </div>

          <div
            v-if="canDeleteBoard"
            class="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
          >
            <p class="mb-3 text-sm font-medium text-red-700 dark:text-red-400">
              {{ t('KANBAN.DELETE_BOARD_TITLE') }}
            </p>
            <p class="mb-3 text-xs text-red-600 dark:text-red-500">
              {{ t('KANBAN.DELETE_BOARD_MESSAGE') }}
            </p>
            <Button
              type="button"
              color="ruby"
              size="sm"
              variant="outline"
              :label="t('KANBAN.DELETE_BOARD')"
              @click="requestDeleteBoard"
            />
          </div>

          <div class="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              color="slate"
              :label="t('KANBAN.CANCEL')"
              @click="closeBoardSettings"
            />
            <Button
              type="submit"
              color="blue"
              :label="t('KANBAN.SAVE_SETTINGS')"
            />
          </div>
        </form>
      </div>
    </Modal>

    <!-- Migrate board modal -->
    <Modal
      v-model:show="isMigrateModalOpen"
      :on-close="() => (isMigrateModalOpen = false)"
    >
      <div class="w-full max-w-[42rem] p-6">
        <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
          {{ t('KANBAN.MIGRATE_BOARD_TITLE') }}
        </h2>
        <p class="mb-5 text-sm text-n-slate-11">
          {{ t('KANBAN.MIGRATE_BOARD_DESCRIPTION') }}
        </p>

        <!-- Step 1: select destination board -->
        <div v-if="migrateStep === 1" class="space-y-4">
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.MIGRATE_SOURCE_LABEL')
            }}</label>
            <div
              class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12"
            >
              {{ currentBoard.name }}
              <span class="ml-2 text-xs text-n-slate-10"
                >({{ totalCards }} {{ t('KANBAN.CARDS_COUNT') }})</span
              >
            </div>
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-n-slate-11">{{
              t('KANBAN.MIGRATE_TARGET_LABEL')
            }}</label>
            <select
              v-model="migrateTargetBoardId"
              class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
            >
              <option value="">{{ t('KANBAN.MIGRATE_NO_TARGET') }}</option>
              <option
                v-for="board in boards.filter(
                  b => b.public_id !== currentBoard.public_id
                )"
                :key="board.public_id"
                :value="board.public_id"
              >
                {{ board.name }}
              </option>
            </select>
          </div>
          <div class="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              color="slate"
              :label="t('KANBAN.CANCEL')"
              @click="isMigrateModalOpen = false"
            />
            <Button
              color="blue"
              :label="t('KANBAN.MIGRATE_NEXT')"
              :disabled="!migrateTargetBoardId"
              @click="advanceMigrateStep"
            />
          </div>
        </div>

        <!-- Step 2: map columns -->
        <div v-else class="space-y-4">
          <p class="text-sm font-medium text-n-slate-11">
            {{ t('KANBAN.MIGRATE_STEP_MAP_COLUMNS') }} —
            <span class="font-semibold text-n-slate-12">{{
              migrateTargetBoard?.name
            }}</span>
          </p>

          <div class="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            <div
              v-for="col in migratableColumns"
              :key="col.public_id"
              class="rounded-xl border border-n-weak bg-n-alpha-1 p-4"
            >
              <!-- Source column info -->
              <div class="mb-3 flex items-center gap-2">
                <span
                  class="h-3 w-3 shrink-0 rounded-full"
                  :style="{ background: col.color || '#3B82F6' }"
                />
                <span class="font-medium text-n-slate-12">{{ col.name }}</span>
                <span class="text-xs text-n-slate-10"
                  >· {{ (col.cards || []).length }}
                  {{ t('KANBAN.CARDS_COUNT') }}</span
                >
              </div>

              <!-- Destination select -->
              <div class="mb-2">
                <label
                  class="mb-1 block text-xs font-medium uppercase tracking-wide text-n-slate-10"
                  >{{ t('KANBAN.MIGRATE_COLUMN_DESTINATION') }}</label
                >
                <select
                  v-model="columnMappings[col.public_id].destination"
                  class="w-full rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm text-n-slate-12"
                >
                  <option value="">
                    {{ t('KANBAN.MIGRATE_NO_MIGRATION') }}
                  </option>
                  <option value="new">
                    {{ t('KANBAN.MIGRATE_NEW_COLUMN') }}
                  </option>
                  <option
                    v-for="targetCol in migrateTargetBoard?.columns || []"
                    :key="targetCol.public_id"
                    :value="targetCol.public_id"
                  >
                    {{ targetCol.name }}
                  </option>
                </select>
              </div>

              <!-- Inherit toggle — only when creating new column -->
              <label
                v-if="columnMappings[col.public_id].destination === 'new'"
                class="flex cursor-pointer items-center gap-2 text-sm"
              >
                <input
                  v-model="columnMappings[col.public_id].inherit"
                  type="checkbox"
                  class="accent-n-brand"
                />
                <span class="text-n-slate-12">{{
                  t('KANBAN.MIGRATE_INHERIT_LABEL')
                }}</span>
                <span class="text-xs text-n-slate-10">{{
                  t('KANBAN.MIGRATE_INHERIT_DESCRIPTION')
                }}</span>
              </label>
            </div>
          </div>

          <div class="flex justify-between gap-2 pt-1">
            <Button
              variant="outline"
              color="slate"
              :label="t('KANBAN.MIGRATE_BACK')"
              @click="migrateStep = 1"
            />
            <div class="flex gap-2">
              <Button
                variant="outline"
                color="slate"
                :label="t('KANBAN.CANCEL')"
                @click="isMigrateModalOpen = false"
              />
              <Button
                color="blue"
                :label="t('KANBAN.MIGRATE_CONFIRM')"
                :disabled="isMigrating"
                @click="migrateBoard"
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <DeleteModal
      v-model:show="isDeleteModalOpen"
      :title="deleteTarget?.title || ''"
      :message="deleteTarget?.message || ''"
      :message-value="deleteTarget?.name || ''"
      :confirm-text="deleteTarget?.confirmText || ''"
      :reject-text="t('KANBAN.CANCEL')"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDelete"
    />
  </div>
</template>
